import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  PersonSearch as PersonSearchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { REGISTRATION_BASE_URL } from '../config/environment';

export default function AdminSyncedCoaches() {
  const [searchParams] = useSearchParams();
  const { getIdToken } = useFirebaseAuth();
  const [usfsSyncUsername, setUsfsSyncUsername] = useState('');
  const [usfsSyncPassword, setUsfsSyncPassword] = useState('');
  const [usfsSyncStates, setUsfsSyncStates] = useState('');
  const [usfsSyncLoading, setUsfsSyncLoading] = useState(false);
  const [usfsSyncResult, setUsfsSyncResult] = useState(null);
  const [usfsSyncError, setUsfsSyncError] = useState(null);
  const [syncedCoaches, setSyncedCoaches] = useState([]);
  const [syncedCoachesLoading, setSyncedCoachesLoading] = useState(false);
  // Initialize search from URL query parameter
  const [syncedCoachesSearch, setSyncedCoachesSearch] = useState(searchParams.get('search') || '');
  const [syncedCoachUpdating, setSyncedCoachUpdating] = useState(null);
  const [matchLinksLoading, setMatchLinksLoading] = useState(false);
  const [matchLinksResult, setMatchLinksResult] = useState(null);
  const [matchLinksError, setMatchLinksError] = useState(null);
  const [matchNamesLoading, setMatchNamesLoading] = useState(false);
  const [matchNamesResult, setMatchNamesResult] = useState(null);
  const [matchNamesError, setMatchNamesError] = useState(null);
  const [reviewSkaters, setReviewSkaters] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogCoach, setLinkDialogCoach] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedLinkUser, setSelectedLinkUser] = useState(null);
  const [linkSubmitLoading, setLinkSubmitLoading] = useState(false);
  const [linkSubmitError, setLinkSubmitError] = useState(null);

  const loadSyncedCoaches = useCallback(async () => {
    setSyncedCoachesLoading(true);
    try {
      const token = await getIdToken(true);
      const res = await fetch(`${REGISTRATION_BASE_URL}/admin/synced-coaches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setSyncedCoaches(data.coaches || []);
    } catch (err) {
      console.error('Load synced coaches failed:', err);
    } finally {
      setSyncedCoachesLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    loadSyncedCoaches();
  }, [loadSyncedCoaches]);

  const handleSyncedCoachActiveChange = useCallback(
    async (coachId, isActive) => {
      setSyncedCoachUpdating(coachId);
      try {
        const token = await getIdToken(true);
        const res = await fetch(
          `${REGISTRATION_BASE_URL}/admin/synced-coaches/${encodeURIComponent(coachId)}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ isActive }),
          }
        );
        if (res.ok) {
          setSyncedCoaches((prev) => prev.map((c) => (c.id === coachId ? { ...c, isActive } : c)));
        }
      } catch (err) {
        console.error('Update coach isActive failed:', err);
      } finally {
        setSyncedCoachUpdating(null);
      }
    },
    [getIdToken]
  );

  const openLinkDialog = useCallback((coach) => {
    setLinkDialogCoach(coach);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedLinkUser(null);
    setLinkSubmitError(null);
    setLinkDialogOpen(true);
  }, []);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkDialogCoach(null);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedLinkUser(null);
    setLinkSubmitError(null);
  }, []);

  const searchUsers = useCallback(
    async (q) => {
      if (!q || q.length < 2) {
        setUserSearchResults([]);
        return;
      }
      setUserSearchLoading(true);
      try {
        const token = await getIdToken(true);
        const res = await fetch(
          `${REGISTRATION_BASE_URL}/admin/users/search?q=${encodeURIComponent(q.trim())}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok) setUserSearchResults(data.users || []);
        else setUserSearchResults([]);
      } catch (err) {
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    },
    [getIdToken]
  );

  useEffect(() => {
    if (!linkDialogOpen) return;
    const t = setTimeout(() => searchUsers(userSearchQuery), 300);
    return () => clearTimeout(t);
  }, [linkDialogOpen, userSearchQuery, searchUsers]);

  const handleLinkSubmit = useCallback(
    async (userId) => {
      if (!linkDialogCoach) return;
      setLinkSubmitError(null);
      setLinkSubmitLoading(true);
      try {
        const token = await getIdToken(true);
        const res = await fetch(
          `${REGISTRATION_BASE_URL}/admin/synced-coaches/${encodeURIComponent(linkDialogCoach.id)}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId: userId || null }),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setLinkSubmitError(data.error || res.statusText || 'Update failed');
          return;
        }
        setSyncedCoaches((prev) =>
          prev.map((c) => (c.id === linkDialogCoach.id ? { ...c, userId: userId || null } : c))
        );
        closeLinkDialog();
      } catch (err) {
        setLinkSubmitError(err.message || 'Request failed');
      } finally {
        setLinkSubmitLoading(false);
      }
    },
    [getIdToken, linkDialogCoach, closeLinkDialog]
  );

  const syncedCoachesFiltered = useMemo(() => {
    const q = (syncedCoachesSearch || '').trim().toLowerCase();
    if (!q) return syncedCoaches;
    return syncedCoaches.filter(
      (c) =>
        (c.displayName || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.usfsMemberId || '').toString().includes(q)
    );
  }, [syncedCoaches, syncedCoachesSearch]);

  const handleSyncUsfsCoaches = async () => {
    setUsfsSyncError(null);
    setUsfsSyncResult(null);
    setUsfsSyncLoading(true);
    try {
      const token = await getIdToken(true);
      const body = {};
      if (usfsSyncUsername.trim()) body.usfsUsername = usfsSyncUsername.trim();
      if (usfsSyncPassword) body.usfsPassword = usfsSyncPassword;
      if (usfsSyncStates.trim()) body.states = usfsSyncStates.trim();
      const res = await fetch(`${REGISTRATION_BASE_URL}/admin/sync-usfs-coaches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUsfsSyncError(data.error || res.statusText || 'Sync failed');
        return;
      }
      const fromDir = data.fromDirectory;
      const fromEMS = data.fromEMS;
      const msg =
        typeof fromDir === 'number' && typeof fromEMS === 'number'
          ? `Synced ${data.synced ?? 0} coaches: ${fromDir} from Coach Directory (published) + ${fromEMS} from EMS (unpublished).`
          : `Synced ${data.synced ?? 0} coaches (${data.total ?? 0} total).`;
      setUsfsSyncResult(msg);
      loadSyncedCoaches();
    } catch (err) {
      setUsfsSyncError(err.message || 'Request failed');
    } finally {
      setUsfsSyncLoading(false);
    }
  };

  const handleMatchLinks = async () => {
    setMatchLinksError(null);
    setMatchLinksResult(null);
    setMatchLinksLoading(true);
    try {
      const token = await getIdToken(true);
      const res = await fetch(`${REGISTRATION_BASE_URL}/admin/match-coach-links`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMatchLinksError(data.error || res.statusText || 'Match failed');
        return;
      }
      setMatchLinksResult(
        `Matched ${data.matched ?? 0} coach record(s) to linked accounts.`
      );
      loadSyncedCoaches();
    } catch (err) {
      setMatchLinksError(err.message || 'Request failed');
    } finally {
      setMatchLinksLoading(false);
    }
  };

  const handleMatchNames = async () => {
    setMatchNamesError(null);
    setMatchNamesResult(null);
    setMatchNamesLoading(true);
    try {
      const token = await getIdToken(true);
      const res = await fetch(`${REGISTRATION_BASE_URL}/admin/match-coach-names`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMatchNamesError(data.error || res.statusText || 'Match failed');
        return;
      }
      setMatchNamesResult(
        `Matched ${data.matched ?? 0} coach record(s) by name (one-time name match).`
      );
      loadSyncedCoaches();
    } catch (err) {
      setMatchNamesError(err.message || 'Request failed');
    } finally {
      setMatchNamesLoading(false);
    }
  };

  const loadSkatersNeedingCoachReview = useCallback(async () => {
    setReviewLoading(true);
    try {
      const token = await getIdToken(true);
      const res = await fetch(`${REGISTRATION_BASE_URL}/admin/skaters-needing-coach-review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setReviewSkaters(data.skaters || []);
    } catch (err) {
      console.error('Load skaters needing coach review failed:', err);
    } finally {
      setReviewLoading(false);
    }
  }, [getIdToken]);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
        Synced coaches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sync coaches from USFS Coach Directory and EMS, then view and manage who appears in the coach dropdown.
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        Sync USFS coaches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pull coaches from the USFS Coach Directory (published) and EMS (unpublished) into the database. Optional:
        enter USFS credentials below, or set USFS_SYNC_USER and USFS_SYNC_PASSWORD on the server.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mb: 3 }}>
        <TextField
          size="small"
          label="USFS username (member #)"
          value={usfsSyncUsername}
          onChange={(e) => setUsfsSyncUsername(e.target.value)}
          placeholder="Optional if set on server"
        />
        <TextField
          size="small"
          type="password"
          label="USFS password"
          value={usfsSyncPassword}
          onChange={(e) => setUsfsSyncPassword(e.target.value)}
          placeholder="Optional if set on server"
        />
        <TextField
          size="small"
          label="States filter (e.g. WA or WA;OR)"
          value={usfsSyncStates}
          onChange={(e) => setUsfsSyncStates(e.target.value)}
          placeholder="Optional"
        />
        <Button
          variant="contained"
          startIcon={usfsSyncLoading ? <CircularProgress size={18} /> : <SyncIcon />}
          onClick={handleSyncUsfsCoaches}
          disabled={usfsSyncLoading}
        >
          {usfsSyncLoading ? 'Syncing…' : 'Sync USFS coaches'}
        </Button>
      </Box>
      {usfsSyncResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUsfsSyncResult(null)}>
          {usfsSyncResult}
        </Alert>
      )}
      {usfsSyncError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUsfsSyncError(null)}>
          {usfsSyncError}
        </Alert>
      )}

      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
        View & manage synced coaches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Coaches from the USFS sync (and linked accounts) appear in the coach dropdown. &quot;Linked&quot; means the
        coach record is tied to an app user. Use &quot;Match links&quot; to backfill by USFS link; use &quot;Match names&quot;
        once to link by name when exactly one user matches. New coaches must provide USFS ID when registering. Turn off
        &quot;Show in dropdown&quot; to hide a coach from selection.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, email, or USFS#"
          value={syncedCoachesSearch}
          onChange={(e) => setSyncedCoachesSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={syncedCoachesLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={loadSyncedCoaches}
          disabled={syncedCoachesLoading}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={matchLinksLoading ? <CircularProgress size={16} /> : <LinkIcon />}
          onClick={handleMatchLinks}
          disabled={matchLinksLoading}
        >
          {matchLinksLoading ? 'Matching…' : 'Match links'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={matchNamesLoading ? <CircularProgress size={16} /> : <PersonSearchIcon />}
          onClick={handleMatchNames}
          disabled={matchNamesLoading}
          title="One-time: link coach records to user accounts by matching names (when exactly one user matches)"
        >
          {matchNamesLoading ? 'Matching…' : 'Match names'}
        </Button>
      </Box>
      {matchLinksResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMatchLinksResult(null)}>
          {matchLinksResult}
        </Alert>
      )}
      {matchLinksError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMatchLinksError(null)}>
          {matchLinksError}
        </Alert>
      )}
      {matchNamesResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMatchNamesResult(null)}>
          {matchNamesResult}
        </Alert>
      )}
      {matchNamesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMatchNamesError(null)}>
          {matchNamesError}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>USFS#</TableCell>
              <TableCell>Compliance</TableCell>
              <TableCell>Linked</TableCell>
              <TableCell align="center">Show in dropdown</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {syncedCoachesLoading && syncedCoaches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : syncedCoachesFiltered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2 }} color="text.secondary">
                  {syncedCoachesSearch
                    ? 'No coaches match your search.'
                    : 'No synced coaches yet. Run Sync USFS coaches above.'}
                </TableCell>
              </TableRow>
            ) : (
              syncedCoachesFiltered.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.displayName || '—'}</TableCell>
                  <TableCell>{c.email || '—'}</TableCell>
                  <TableCell>{c.usfsMemberId || c.id}</TableCell>
                  <TableCell>{c.usfsComplianceStatus || '—'}</TableCell>
                  <TableCell>{c.userId ? 'Yes' : '—'}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={c.isActive !== false}
                      disabled={syncedCoachUpdating === c.id}
                      onChange={() => handleSyncedCoachActiveChange(c.id, !(c.isActive !== false))}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {c.userId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkOffIcon />}
                        onClick={() => openLinkDialog(c)}
                        sx={{ textTransform: 'none' }}
                      >
                        Unlink
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => openLinkDialog(c)}
                        sx={{ textTransform: 'none' }}
                      >
                        Link to user
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {syncedCoaches.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {syncedCoachesFiltered.length} of {syncedCoaches.length} coaches
          {syncedCoachesSearch ? ' (filtered)' : ''}
        </Typography>
      )}

      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 4 }}>
        Skaters needing coach review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Skaters whose coach is not in the synced/approved coach list are flagged. Run the check to refresh the list and
        set the flag on each skater. Fix by editing the skater and selecting a coach from the dropdown.
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={reviewLoading ? <CircularProgress size={16} /> : <WarningIcon />}
        onClick={loadSkatersNeedingCoachReview}
        disabled={reviewLoading}
        sx={{ mb: 2 }}
      >
        {reviewLoading ? 'Checking…' : 'Check skaters'}
      </Button>
      {reviewSkaters.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Skater</TableCell>
                <TableCell>USFS#</TableCell>
                <TableCell>Invalid coach reference(s)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewSkaters.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    {s.firstName} {s.lastName}
                  </TableCell>
                  <TableCell>{s.membershipId || '—'}</TableCell>
                  <TableCell>
                    {(s.invalidCoachRefs || [])
                      .map((c) => c.displayName || c.email || c.coachId || 'Unknown')
                      .join(', ') || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {reviewSkaters.length === 0 && !reviewLoading && (
        <Typography variant="body2" color="text.secondary">
          No skaters needing coach review, or run &quot;Check skaters&quot; to refresh.
        </Typography>
      )}

      <Dialog open={linkDialogOpen} onClose={closeLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {linkDialogCoach?.userId ? 'Unlink coach from user' : 'Link coach to app user'}
        </DialogTitle>
        <DialogContent>
          {linkDialogCoach && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Coach: <strong>{linkDialogCoach.displayName || '—'}</strong>
              {linkDialogCoach.usfsMemberId && ` (USFS# ${linkDialogCoach.usfsMemberId})`}
            </Typography>
          )}
          {linkDialogCoach?.userId ? (
            <Typography variant="body2">
              This will remove the link between this coach record and the app user. The coach will still appear in the
              list but will show as not linked.
            </Typography>
          ) : (
            <Autocomplete
              options={userSearchResults}
              getOptionLabel={(opt) => (opt ? `${opt.displayName || '—'} (${opt.email || '—'})` : '')}
              loading={userSearchLoading}
              inputValue={userSearchQuery}
              onInputChange={(_, value) => setUserSearchQuery(value)}
              value={selectedLinkUser}
              onChange={(_, value) => setSelectedLinkUser(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by name or email"
                  placeholder="Type 2+ characters"
                  size="small"
                />
              )}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              sx={{ mt: 1 }}
            />
          )}
          {linkSubmitError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setLinkSubmitError(null)}>
              {linkSubmitError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkDialog}>Cancel</Button>
          {linkDialogCoach?.userId ? (
            <Button
              color="warning"
              variant="contained"
              onClick={() => handleLinkSubmit(null)}
              disabled={linkSubmitLoading}
              startIcon={linkSubmitLoading ? <CircularProgress size={16} /> : <LinkOffIcon />}
            >
              {linkSubmitLoading ? 'Unlinking…' : 'Unlink'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => selectedLinkUser && handleLinkSubmit(selectedLinkUser.id)}
              disabled={!selectedLinkUser || linkSubmitLoading}
              startIcon={linkSubmitLoading ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {linkSubmitLoading ? 'Linking…' : 'Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
